import { ArrowDownCircleIcon } from "@heroicons/react/24/solid";
import React, { useState, useEffect } from "react";
import { useQueryClient } from "react-query";
import UploadFileButton from "../../../components/UploadFileButton";
import {
  downloadProdCodeTemplate,
  massUploadProducerCode,
  validateMassUpload,
} from "../../../actions/CarrierAgreementMgmt/ProducerCodeConfig";
import Modal from "../../../components/Modal";
import ConfirmBoxDelete from "../../../components/ConfirmBoxDelete";
import EditButton from "../../../components/EditButton";
import HideButton from "../../../components/HideButton";
import { MassUploadTable } from "./MassUploadTable";
import { MassUploadExpandedRowShow } from "./MassUploadExpandedRowShow";
import SaveDataButton from "../../../components/SaveDataButton";
import { toast } from "../../../components/toastNotification";
import { useRecoilValue } from "recoil";
import { accountAtom } from "../../Login/atoms/AccountAtom";
import ShowButton from "../../../components/ShowButton";
import { useGetMGA } from "./hooks/useGetMGA";
import { useCarrierProgram } from "../../Utilities/ProducerConfiguration/CarrierProgram/hooks/useCarrierProgram";
import { useGetCarriers } from "../Carrier/hooks/useGetCarriers";
import { useLobTypeOptions } from "./hooks/useLobTypeOptions";
import { useDistributionChannelOptions } from "./hooks/useDistributionChannelOptions";
import useGetEmployees from "./hooks/useGetEmployees";
import { useCodeLevel } from "./hooks/useCodeLevel";
import { useGetProdType } from "./hooks/useProducerCodeType";
import ReactTooltip from "react-tooltip";
import { useCarrierProducerCodeList } from "./hooks/useGetCarrierProdCode";

const MassUpload = ({ isOpen, setMassUploadModal, groupedProducerCodes }) => {
  localStorage.setItem("ModalSelected", "");
  const { data: allProducerCodeList } =
    useCarrierProducerCodeList(groupedProducerCodes);
  const [xlsx, setXlsx] = useState(null);
  const [selectedData] = useState();
  const [allData, setAllData] = useState([]);
  const [validData, setValidData] = useState([]);
  const [invalidData, setInvalidData] = useState([]);
  const [selectedTab, setSelectedTab] = useState("All");
  const [franchiseCheckBoxValue] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const queryClient = useQueryClient();
  const [showEdit, setShowEdit] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleClear, setToggleCleared] = useState(false);
  const [origData, setOrigData] = useState([]);
  const [buttonSaveText, setButtonSaveText] = useState("Save Data");
  const [show, setShow] = useState(false);
  const [DisplayNote, setDisplayNote] = useState(false);
  const user = useRecoilValue(accountAtom);
  const { data: prodTypes } = useGetProdType();
  const prodTypesOptions = prodTypes
    ?.filter((ptypes) => ptypes.isActive === true)
    .map((x) => {
      return { label: x.producerCodeTypesName, value: x.producerCodeTypesId };
    });
  const { data: codeLevels } = useCodeLevel();
  const codeLevelOptions = codeLevels
    ?.filter((cLevels) => cLevels.isActive === true)
    .map((x) => {
      return { label: x.codeLevelName, value: x.codeLevelId };
    });
  const { data: carriersQuery, isLoading: carrierLoading } = useGetCarriers();
  const carriers =
    carriersQuery &&
    carriersQuery.map((resData) => ({
      label: resData.carrierName,
      value: resData.masterCarrierId,
    }));
  const { data: employeeQuery } = useGetEmployees();

  const { data: LOBQuery } = useLobTypeOptions();
  const loboptions =
    LOBQuery &&
    LOBQuery.filter((data) => data.isActive === true).map((resData) => ({
      label: resData.lobname,
      value: resData.lobid,
    }));

  useEffect(() => {
    const scriptShim = document.createElement("script");
    scriptShim.async = true;
    scriptShim.src =
      "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.3/shim.min.js";
    scriptShim.integrity =
      "824a6ade25af712b2dabb1dc9d30f3441b5b1652fd7fa43a9f6ba99607849a6334c1baf0085047e4d2692a06e4ed299af067d71ffdf7986225cc041c6d8d71b8"; //C0nF!esH1m //sha512
    scriptShim.crossOrigin = "anonymous";

    const scriptXLS = document.createElement("script");
    scriptXLS.async = true;
    scriptXLS.src =
      "https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.17.3/xlsx.full.min.js";
    scriptXLS.integrity =
      "ce7e2434109f7c48061d1a110f5d2082e827dd41a0d9bb3950be8e3adea03f7508aa287b5c177754cf32dab771212d7de2f03d8a19d685b0dfc8a8452fd8172f"; //C0nF!eXLs //sha512
    scriptXLS.crossOrigin = "anonymous";

    scriptXLS.onload = () => setXlsx(window["XLSX"]);

    document.body.appendChild(scriptShim);
    document.body.appendChild(scriptXLS);

    let timer = setTimeout(() => setLoading(false), 2000);

    return () => {
      clearTimeout(timer);
    };
  }, []);
  const { data: distChannelQuery } = useDistributionChannelOptions();
  const distChannelOpts =
    distChannelQuery &&
    distChannelQuery
      .filter(
        (dist) =>
          dist.isActive === true &&
          (user?.isNonFranchise && user?.isFranchise
            ? dist.distributionChannelName !== null
            : user?.isFranchise
            ? dist.distributionChannelName !== null
            : dist.distributionChannelName !== "Franchise")
      )
      .map((data) => ({
        label: data.distributionChannelName,
        value: data.distributionChannelId,
      }));

  const mgaList = useGetMGA().data?.map((x) => {
    return {
      label: x.mga1,
      value: x.mga1,
    };
  });
  const carrierProgramOptions = useCarrierProgram().data?.map((x) => {
    return {
      label: x.programName,
      value: x.programName,
      carrierId: x.carrierId,
      isActive: x.isActive,
    };
  });

  const UploadFile = (e) => {
    if (e.target.files.length > 0) {
      var reader = new FileReader();
      var excelFile = e.target.files[0];
      reader.onload = (e) => {
        var result = e.target.result;
        try {
          var wb = xlsx.read(result, { type: "binary" });
          const xData = xlsx.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]]);
          if (
            xData.some((item) =>
              item.hasOwnProperty("Carrier Name required")
            ) &&
            xData.some((item) =>
              item.hasOwnProperty("Producer Code required")
            ) &&
            !xData.some((item) => item.hasOwnProperty("Address"))
          ) {
            if (
              xData[0].hasOwnProperty("Carrier Name required") &&
              xData[0].hasOwnProperty("Producer Code required") &&
              xData[0].hasOwnProperty("Line of Business required") &&
              xData[0].hasOwnProperty("Producer Code Type required") &&
              xData[0].hasOwnProperty("Code Level required")
            ) {
              var forValidation = xData.map((prodCode) => {
                // let isnum = /^\d+$/.test(
                //   prodCode["Store Number required if associated to Office"]
                // );
                return {
                  carrierName: prodCode["Carrier Name required"]
                    ? prodCode["Carrier Name required"].toString()
                    : "",
                  producerCode: prodCode["Producer Code required"]
                    ? String(prodCode["Producer Code required"])
                    : "",
                  producerSubCodes: prodCode["Producer SubCode"]
                    ? String(prodCode["Producer SubCode"])
                    : "",
                  lineOfBusiness: prodCode["Line of Business required"]
                    ? prodCode["Line of Business required"].toString()
                    : "",
                  storeNumber: prodCode[
                    "Store Number required if associated to Office"
                  ]
                    ? prodCode["Store Number required if associated to Office"]
                    : "",
                  officeCode: prodCode[
                    "Office Code required if associated to Office"
                  ]
                    ? String(
                        prodCode["Office Code required if associated to Office"]
                      )
                    : "",

                  producer: prodCode[
                    "Employee Code required if associated to Producer"
                  ]
                    ? String(
                        prodCode[
                          "Employee Code required if associated to Producer"
                        ]
                      )
                    : "",
                  mAproducerCodes: prodCode["MA Producer Code"]
                    ? String(prodCode["MA Producer Code"])
                    : "",
                  program: prodCode["Carrier Program"]
                    ? String(prodCode["Carrier Program"])
                    : "",
                  mga: prodCode["MGA"] ? String(prodCode["MGA"]) : "",
                  producerCodeType: prodCode["Producer Code Type required"]
                    ? String(prodCode["Producer Code Type required"])
                    : "",
                  masterCode: prodCode["Master Code"]
                    ? String(prodCode["Master Code"])
                    : "",
                  masterCodeStatus: prodCode[
                    "Master Code Status (Active/Inactive)"
                  ]
                    ? String(prodCode["Master Code Status (Active/Inactive)"])
                    : "",
                  codeLevel: prodCode["Code Level required"]
                    ? String(prodCode["Code Level required"])
                    : "",
                  issuedTo: prodCode["Employee Codes Issued To"]
                    ? String(prodCode["Employee Codes Issued To"])
                    : "",
                  crossSale: prodCode["Can Sell"]
                    ? String(prodCode["Can Sell"])
                    : "",
                  rollUpCode: prodCode["Roll Up Code"]
                    ? String(prodCode["Roll Up Code"])
                    : "",
                  rollUpCodeStatus: prodCode[
                    "Roll Up Code Status (Active/Inactive)"
                  ]
                    ? String(prodCode["Roll Up Code Status (Active/Inactive)"])
                    : "",
                  distributionChannel: prodCode["Distribution Channel"]
                    ? String(prodCode["Distribution Channel"])
                    : "",
                  isFranchise:
                    user.isFranchise && user.isNonFranchise
                      ? false
                      : user.isFranchise,
                };
              });
              ValidateUploadedFile(forValidation);
              setDisplayNote(true);
            } else {
              toast.fire(
                "Uploading Producer Code Failed",
                "Please fill in all the required fields",
                "error"
              );

              setDisplayNote(true);
            }
          } else {
            toast.fire(
              "Uploading Producer Code Failed",
              "Invalid file or missing required columns, please check file and re-upload again",
              "error"
            );
          }
        } catch (err) {
          toast.fire(
            "Uploading Producer Code Failed",
            "Invalid file, please check file and re-upload again",
            "error"
          );
        }
      };
      reader.readAsBinaryString(excelFile);
      e.target.value = null;
    }
  };

  const updateRow = (currentData) => {
    const updatedData = origData.map((data) => {
      if (currentData.id === data.id) {
        data = currentData;
      }
      return data;
    });
    setAllData(updatedData);
    setOrigData(updatedData);
  };

  const ValidateUploadedFile = (uploadedData) => {
    setLoading(true);
    validateMassUpload(uploadedData).then((res) => {
      if (res) {
        setAllData(res);
        setOrigData(res);
        var tempValidData = [];
        var tempInvalidData = [];
        res.map((prodCode) => {
          if (prodCode.isValid) tempValidData.push(prodCode);
          else tempInvalidData.push(prodCode);
        });
        setValidData(tempValidData);
        setInvalidData(tempInvalidData);
      } else {
        toast.fire(
          "Server Error",
          "Failed to upload file, please check server or internet connection",
          "error"
        );
      }
      setLoading(false);
      setSelectedTab("All");
    });
  };

  useEffect(() => {
    if (!isOpen) {
      setAllData([]);
      setOrigData([]);
      setInvalidData([]);
      setValidData([]);
    }
  }, [isOpen]);

  const SaveMassUploadData = () => {
    setButtonSaveText("Saving...");
    const prodCodes = validData.map((x) => {
      return {
        ...x,
        createdBy: user.username,
        modifiedBy: user.username,
        isFranchise:
          user.isFranchise && user.isNonFranchise
            ? franchiseCheckBoxValue
            : user.isFranchise,
      };
    });
    massUploadProducerCode(prodCodes)
      .then((res) => {
        queryClient.invalidateQueries("ProducerCode");
        if (res.status === 200) {
          if (invalidData.length > 0) {
            setAllData(origData.filter((data) => !data.isValid));
            setOrigData(origData.filter((data) => !data.isValid));
            toast.fire(
              "Saved Successfully",
              "Your data has been saved",
              "success"
            );
            setButtonSaveText("Save Data");
          } else {
            setAllData([]);
            setInvalidData([]);
            setValidData([]);
            setMassUploadModal(false);
            toast.fire(
              "Saved Successfully",
              "Your data has been saved",
              "success"
            );
          }
        }
        if (res.status === 400 || res.status !== 200) {
          const errMessage = res.data.title ? res.data.title : "Server Error";

          toast.fire("Saving Failed", errMessage, "error");
        }
        setButtonSaveText("Save Data");
      })
      .catch((e) => {
        toast.fire(
          "Save Failed",
          "We encountered an error when saving your data!",
          "error"
        );
      });
  };

  const DownloadTemplate = () => {
    downloadProdCodeTemplate().then((content) => {
      if (content.status === 200) {
        var url = window.URL.createObjectURL(content.data);
        let templateName =
          user.isFranchise && user.isNonFranchise
            ? "MassUploadProducerCodeTemplate.xlsx"
            : user.isFranchise
            ? "MassUploadProducerCodeTemplate_Franchise.xlsx"
            : "MassUploadProducerCodeTemplate.xlsx";
        let tempLink = document.createElement("a");
        tempLink.href = url;
        tempLink.setAttribute("download", templateName);
        tempLink.click();

        toast.fire("", "File downloaded successfully", "success");
      }
      if (content.status === 404) {
        toast.fire("Error downloading file:", "File not found", "error");
      }
    });
  };

  const handleActions = (row, type) => {
    const updatedData = origData.map((d) => {
      if (d.id === row.id) {
        d.expandRow = !d.expandRow;
      }
      return d;
    });

    if (type === "Edit") {
      setShow(updatedData);
      setShowEdit(true);
      setDisplayNote(false);
    } else if (type === "Show") {
      setShow(true);
      setShowEdit(false);
      setDisplayNote(false);
    } else {
      setShow(false);
      setShowEdit(false);
      setDisplayNote(true);
    }
  };

  const handleDelete = () => {
    const ids = selectedRows.map((d) => d.id);
    const newData = origData.filter((d) => !ids.includes(d.id));
    setAllData(newData);
    setOrigData(newData);
    setOpenModal(false);
    setSelectedRows([]);
    setToggleCleared(!toggleClear);
    if (selectedTab === "Invalid" || selectedTab === "Valid") {
      handleSelectedTab(selectedTab, newData);
    }
  };
  const handleSelectedRowsChange = (state) => {
    setSelectedRows(state.selectedRows);
  };

  const columns = [
    {
      name: "Findings",
      left: "true",
      cell: (row) => {
        return (
          <div className=" items-left text-left">
            {row.isValid ? (
              <>
                {row.warnings !== "" ? (
                  <>
                    <ReactTooltip place="right" effect="solid" type="info" />
                    <div
                      className=" items-left text-left"
                      data-tip={row.warnings}
                    >
                      Valid{" "}
                      {" (" + row.warnings.substring(0, 12).trim() + "...)"}
                    </div>
                  </>
                ) : (
                  <>
                    <div className=" items-left text-left"> Valid</div>
                  </>
                )}{" "}
              </>
            ) : (
              <>
                <ReactTooltip place="right" effect="solid" type="info" />{" "}
                <div data-tip={row.findings + row.warnings}>
                  {row.findings.length > 20 ? (
                    <>{row.findings.substring(0, 20).trim() + "..."}</>
                  ) : (
                    <> {row.findings}</>
                  )}
                </div>
              </>
            )}
          </div>
        );
      },
    },
    {
      name: "Carrier",
      cell: (row) => {
        return <div className=" items-left text-left">{row.carrierName}</div>;
      },
    },
    {
      name: "Office Code",
      screenLeft: true,
      cell: (row) => {
        return <div className="">{row.officeCode}</div>;
      },
    },
    {
      name: "Store Number",
      cell: (row) => {
        return <div className="">{row.storeNumber}</div>;
      },
    },
    {
      name: "Producer Code",
      cell: (row) => {
        return <div className=" ">{row.producerCode}</div>;
      },
    },
    {
      name: "Code Level",
      cell: (row) => {
        return <div className="">{row.codeLevel}</div>;
      },
    },
    {
      name: "Line of Business",
      cell: (row) => {
        return <div className=" ">{row.lineOfBusiness}</div>;
      },
    },
    {
      name: "MA Producer Code",
      cell: (row) => {
        return <div className=" ">{row.mAproducerCodes}</div>;
      },
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => {
        return (
          <div className="flex 2xl:flex-row flex-col justify-center items-center">
            <div className="flex flex-row">
              {row.expandRow ? (
                <HideButton
                  buttonText="Hide"
                  onClick={() => {
                    handleActions(row, "Hide");
                  }}
                />
              ) : (
                ""
              )}
              <div className="flex flex-row flex-col justify-center items-center opacity-100 gap-x-2 space-x-1 space-y-1">
                {row.expandRow ? (
                  ""
                ) : (
                  <ShowButton
                    className={
                      (show || showEdit || selectedRows.length > 0
                        ? "opacity-50"
                        : "opacity-100") + ""
                    }
                    disabled={
                      show || showEdit || selectedRows.length > 0 ? true : false
                    }
                    buttonText="Show"
                    onClick={() => {
                      handleActions(row, "Show");
                    }}
                  />
                )}
                {row.expandRow ? (
                  ""
                ) : (
                  <EditButton
                    className={
                      (show || selectedRows.length > 0
                        ? "opacity-50 gap-2"
                        : "opacity-100 gap-2") + ""
                    }
                    disabled={
                      show || showEdit || selectedRows.length > 0 ? true : false
                    }
                    buttonText="Edit"
                    onClick={() => {
                      handleActions(row, "Edit");
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      },
    },
  ];

  const tabs = ["All", "Valid", "Invalid"];
  const handleSelectedTab = (tab, data) => {
    setSelectedTab(tab);
    // eslint-disable-next-line default-case
    switch (tab) {
      case "All":
        return setAllData(origData);
      case "Valid":
        if (data !== null) {
          const valids = data.filter((data) => data.isValid);
          return setAllData(valids);
        } else {
          setSelectedRows([]);
          const valids = origData.filter((data) => data.isValid);
          return setAllData(valids);
        }

      case "Invalid":
        if (data !== null) {
          const invalids = data.filter((data) => !data.isValid);
          return setAllData(invalids);
        } else {
          setSelectedRows([]);
          const invalids = origData.filter((data) => !data.isValid);
          return setAllData(invalids);
        }
    }
  };

  const closeModal = () => {
    setMassUploadModal(false);
    setShow(false);
    setShowEdit(false);
    setDisplayNote(true);
    setSelectedRows([]);
    setOrigData([]);
    setSelectedTab("All");
  };
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        closeModal();
      }}
      closeOutside={false}
    >
      <ConfirmBoxDelete
        ModalTitle="Delete Producer Code"
        qConfirmation={"Are you sure you want to delete producer code(s)?"}
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        onClickFunction={handleDelete}
      />
      <div className="">
        <div className="font-bold text-xl flex items-center m-2">
          <div className="text-base text-primary font-bold">
            Producer Code Mass Upload
          </div>
          <div className="ml-auto flex items-center">
            <button
              className="hover:scale-90 transition duration-500 bg-tranparent mr-2 border-secondary shadow-md hover:bg-secondary text-secondary group relative flex justify-center items-center py-1 px-3 border text-xs font-medium rounded-full hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary focus:ring-opacity-70"
              onClick={DownloadTemplate}
              disabled={show || showEdit || selectedRows.length > 0}
            >
              <ArrowDownCircleIcon className="h-4 w-4 mr-2" />
              Template
            </button>
            {/* <label
              className="hover:scale-90 transition duration-500 shadow-md border-quinary bg-tranparent hover:bg-quinary 
                  text-quinary group relative flex justify-center py-1 px-3 border  text-xs font-medium rounded-full hover:text-white 
                  focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-quinary focus:ring-opacity-70"
            >
              <ArrowUpCircleIcon className="h-4 w-4 mr-2" />
              Upload file
              <input
                type="file"
                disabled={show || showEdit || selectedRows.length > 0}
                className="hidden"
                onChange={UploadFile}
              />
            </label> */}
            <UploadFileButton onChange={UploadFile} disabled={false} />
          </div>
        </div>
        <div className="mt-2 h-1 w-full border-b-4 border-secondary" />
        <div>
          <div className="flex justify-between">
            <div>
              {tabs.map((tab) => {
                return (
                  <button
                    key={tab}
                    onClick={() => handleSelectedTab(tab, null)}
                    className={`border-b-4 font-medium text-sm text-tertiary cursor-pointer transition py-3 px-5  
                     ${
                       selectedTab === tab
                         ? "border-primary font-bold bg-gray-50"
                         : "border-transparent hover:bg-gray-50 "
                     }`}
                  >
                    {tab}
                  </button>
                );
              })}
            </div>
            <div className="flex items-end justify-end gap-2">
              {(selectedTab === "Valid" || selectedTab === "All") &&
              !show &&
              !showEdit
                ? validData.length > 0 && (
                    <div className="ml-auto">
                      <SaveDataButton
                        className={
                          show ||
                          showEdit ||
                          (selectedRows.length === 0 &&
                            buttonSaveText === "Save Data")
                            ? "opacity-100"
                            : "opacity-50 cursor-not-allowed border"
                        }
                        disabled={
                          show ||
                          showEdit ||
                          (selectedRows.length === 0 &&
                            buttonSaveText === "Save Data")
                            ? false
                            : true
                        }
                        onClick={SaveMassUploadData}
                        buttonValue={buttonSaveText}
                      ></SaveDataButton>
                    </div>
                  )
                : invalidData.length > 0 && ""}
              {/* <div>
                {user.isFranchise &&
                  user.isNonFranchise &&
                  DisplayNote &&
                  allData.length > 0 && (
                    <div className="flex justify-between gap-2">
                      <ReactTooltip
                        id="IsFranchise"
                        place="left"
                        effect="solid"
                        type="info"
                        className="font-normal"
                      />
                      <input
                        type="checkbox"
                        data-tip={
                          "Note: Changing data category will reset the validation of all data."
                        }
                        data-for="IsFranchise"
                        className=" flex w-5 h-5 items-center space-x-1 cursor-pointer"
                        checked={franchiseCheckBoxValue}
                        onChange={(e) => {
                          //  clickcode
                          handleSuperUserIsFranchise();
                        }}
                      />
                      <label className="cursor-pointer text-sm" name="">
                        Franchise
                      </label>
                    </div>
                  )}
              </div> */}
            </div>
          </div>
          <div className="flex flex-row  justify-between items-center pb-1">
            {DisplayNote && allData.length >= 1 ? (
              <div className="text-secondary italic text-xs3 mt-4">
                <p className="font-bold italic inline-block mr-1">Note: </p>
                Tick the box to delete some data.
              </div>
            ) : (
              ""
            )}
          </div>
          <div className="mt-5">
            <MassUploadTable
              data={allData}
              selectableRows={DisplayNote && selectedRows}
              columns={columns}
              loading={loading}
              isNote={DisplayNote}
              expandableRowsComponent={(row) => (
                <MassUploadExpandedRowShow
                  data={row.data}
                  isEdit={showEdit}
                  Cancel={() => handleActions(row.data, "Hide")}
                  updateRow={(data) => updateRow(data)}
                  setShowEdit={setShowEdit}
                  setOrigData={setOrigData}
                  setAllData={setAllData}
                  origData={origData}
                  setValidData={setValidData}
                  setInvalidData={setInvalidData}
                  setShow={setShow}
                  loboptions={loboptions}
                  distChannelOpts={distChannelOpts}
                  setDisplayNote={setDisplayNote}
                  selectedTab={selectedTab}
                  handleSelectedTab={handleSelectedTab}
                  carriers={carriers}
                  mgaList={mgaList}
                  carrierProgramOptions={carrierProgramOptions}
                  carrierLoading={carrierLoading}
                  employeeOptions={employeeQuery}
                  codeLevelOptions={codeLevelOptions}
                  prodTypesOptions={prodTypesOptions}
                  isFranchise={franchiseCheckBoxValue}
                  allProducerCodeList={allProducerCodeList}
                />
              )}
              expandableRowsComponentProps={selectedData}
              expandableRowExpanded={(row) => row.expandRow}
              onSelectedRowsChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              setOpenModal={setOpenModal}
              clearSelectedRows={toggleClear}
              allProducerCodeList={allProducerCodeList}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MassUpload;
